import React, { useState, useEffect, useRef } from "react";
import { Form, Row, Col, Button, InputGroup } from "react-bootstrap";
import { toast } from "react-toastify";
import './Filters.css';
import {
    IconX,
    IconRefresh,
    IconCalendarWeek,
    IconBriefcase2,
    IconBolt,
    IconCalendarTime,
    IconArrowRight,
    IconBox,
} from "@tabler/icons-react";
import DatePicker from "react-datepicker";
import { TreeSelect } from "primereact/treeselect";
import usePostRequest from "../../../hooks/usePostRequest";
import { API_ENDPOINTS } from "../../../constant/_constant";
import ProductSelectModal from "./ProductSelectModal";
import moment from "moment";

const STATUS_OPTIONS = [
    { label: "Draft", value: "DRAFT" },
    { label: "Pending", value: "PENDING" },
    { label: "Won", value: "WON" },
    { label: "Lost", value: "LOST" },
    { label: "Cancelled", value: "CANCELLED" },
];

const initState = {
    salesLead: "",
    contractVehicle: "",
    orderType: "",
    paymentType: "",
    status: "",
    vendor: "",
    vendor_name: "",
    selectedProducts: [],
    createdFrom: null,
    createdTo: null,
    publishedFrom: null,
    publishedTo: null,
    dueDateFrom: null,
    dueDateTo: null,
};

const LS_KEY = "opportunityFilterPanel";

const SectionHeader = ({ icon: Icon, type = "general", label }) => {
    const typeStyles = {
        general: { bg: "bg-orange-lt", text: "text-indigo" },
        specific: { bg: "bg-indigo-lt", text: "text-indigo" },
        dates: { bg: "bg-green-lt", text: "text-indigo" },
    };
    const styles = typeStyles[type] || typeStyles.general;

    return (
        <div className="filters-section-wrap">
            <div className="filters-section-label">
                <div className={`bagde_filters  ${styles.bg} d-flex align-items-center justify-content-center section-icon`}>
                    <Icon size={12} className={styles.text} />
                </div>
                <span className={styles.text}>{label}</span>
                <div className="filters-section-divider" />
            </div>
        </div>
    );
};

function Filters({
    show,
    onHide,
    initialValues = {},
    salesHeadData = [],
    contractData = [],
    onApply,
    matchCount = 0,
    totalCount = 0,
    anchorRef,
}) {
    const [f, setF] = useState(() => {
        try {
            const saved = localStorage.getItem(LS_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    ...initState,
                    ...parsed,
                    createdFrom: parsed.createdFrom ? new Date(parsed.createdFrom) : null,
                    createdTo: parsed.createdTo ? new Date(parsed.createdTo) : null,
                    publishedFrom: parsed.publishedFrom ? new Date(parsed.publishedFrom) : null,
                    publishedTo: parsed.publishedTo ? new Date(parsed.publishedTo) : null,
                    dueDateFrom: parsed.dueDateFrom ? new Date(parsed.dueDateFrom) : null,
                    dueDateTo: parsed.dueDateTo ? new Date(parsed.dueDateTo) : null,
                };
            }
        } catch {
            // ignore parse errors
        }
        return initState;
    });

    const [showProductModal, setShowProductModal] = useState(false);
    const panelRef = useRef(null);

    const { data: vendorlist, postData: fetchVendorList } = usePostRequest(
        API_ENDPOINTS.GET_VENDORS
    );

    useEffect(() => {
        fetchVendorList({ page: 1, limit: 500, is_relevent: true });
    }, []);

    useEffect(() => {
        if (show) setF((p) => ({ ...initState, ...initialValues }));
    }, [show]);

    useEffect(() => {
        if (!show) return;
        const handleClickOutside = (e) => {
            if (
                panelRef.current &&
                !panelRef.current.contains(e.target) &&
                anchorRef?.current &&
                !anchorRef.current.contains(e.target)
            ) {
                if (
                    e.target.closest(".p-treeselect-panel") ||
                    e.target.closest(".react-datepicker") ||
                    e.target.closest(".modal")
                ) return;
                onHide();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [show, onHide, anchorRef]);

    useEffect(() => {
        try {
            localStorage.setItem(LS_KEY, JSON.stringify(f));
        } catch {
            // ignore storage errors
        }
    }, [f]);

    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

    const handleReset = () => {
        setF(initState);
        localStorage.removeItem(LS_KEY);
    };

    const handleApply = () => {
        const pairs = [
            ["createdFrom", "createdTo", "Created Date"],
            ["publishedFrom", "publishedTo", "Published Date"],
            ["dueDateFrom", "dueDateTo", "Due Date"],
        ];

        for (const [a, b, label] of pairs) {
            if ((f[a] && !f[b]) || (!f[a] && f[b])) {
                toast.error(`Please select both "from" and "to" for ${label}`);
                return;
            }
        }

        try {
            localStorage.setItem(LS_KEY, JSON.stringify(f));
        } catch {
            // ignore storage errors
        }
        onApply?.(f);
        onHide();
    };

    const treeOptions = [
        { key: "", label: "All", selectable: true, children: [] },
        ...(contractData || []).map((vehicle) => ({
            key: String(vehicle.id),
            label: vehicle.name,
            selectable: true,
            children:
                vehicle.children?.map((child) => ({
                    key: String(child.id),
                    label: child.name,
                    selectable: true,
                    children: [],
                })) || [],
        })),
    ];

    const dateInput = (val, onChange, minDate, maxDate) => (
        <InputGroup>
            <InputGroup.Text>
                <IconCalendarWeek />
            </InputGroup.Text>
            <DatePicker
                className="form-control"
                selected={val}
                placeholderText="Select Date"
                onChange={onChange}
                minDate={minDate}
                maxDate={maxDate}
                showMonthDropdown
                showYearDropdown
            />
        </InputGroup>
    );

    const dateRangeRow = (label, fromVal, toVal, fromKey, toKey, maxDate) => (
        <Row className="g-2 mb-3 align-items-end">
            <Col xs={5}>
                <div className="filters-date-field-label">{label} from</div>
                {dateInput(fromVal, (d) => set(fromKey, d), undefined, maxDate || undefined)}
            </Col>
            <Col xs={1} className="filters-arrow-col">
                <IconArrowRight size={16} className="text-secondary" />
            </Col>
            <Col xs={6}>
                <div className="filters-date-field-label">{label} to</div>
                {dateInput(toVal, (d) => set(toKey, d), fromVal || undefined, maxDate || undefined)}
            </Col>
        </Row>
    );

    const handleVendorChange = (e) => {
        const selectedId = e.target.value;
        const selectedVendor = (vendorlist?.data || []).find(
            (v) => String(v.id) === String(selectedId)
        );
        setF((prev) => ({
            ...prev,
            vendor: selectedId,
            vendor_name: selectedVendor ? selectedVendor.display_name : "",
        }));
    };

    if (!show) return null;

    return (
        <>
            <ProductSelectModal
                show={showProductModal}
                onHide={() => setShowProductModal(false)}
                selectedProducts={f.selectedProducts || []}
                onApply={(products) => set("selectedProducts", products)}
            />

            <div ref={panelRef} className="filters-dropdown-panel">
                <div className="filters-header">
                    <h5 className="filters-title">Refine Opportunities</h5>
                    <div className="d-flex align-items-center gap-3">
                        <Button
                            variant="link"
                            className="filters-reset-btn text-secondary"
                            onClick={handleReset}
                        >
                            <IconRefresh size={14} /> Reset all
                        </Button>
                        <span role="button" onClick={onHide} className="text-secondary" style={{ cursor: "pointer" }}>
                            <IconX size={20} />
                        </span>
                    </div>
                </div>

                <div className="filters-body">
                    <SectionHeader icon={IconBriefcase2} type="general" label="GENERAL" />

                    <Row className="g-3 mb-2">
                        <Col xs={4}>
                            <div className="filters-field-label">Sales Lead</div>
                            <Form.Select
                                className="filters-input"
                                value={f.salesLead}
                                onChange={(e) => set("salesLead", e.target.value)}
                            >
                                <option value="">Select</option>
                                {(salesHeadData || []).map((s, index) => (
                                    <option key={s.id || index} value={s.email}>{s.email}</option>
                                ))}
                            </Form.Select>
                        </Col>

                        <Col xs={4}>
                            <div className="filters-field-label">Contract Vehicle</div>
                            <TreeSelect
                                value={f.contractVehicle ? String(f.contractVehicle) : null}
                                onChange={(e) => set("contractVehicle", e.value ?? "")}
                                options={treeOptions}
                                placeholder="Select"
                                filter
                                filterPlaceholder="Search contract vehicle"
                                className="w-100 filter-treeselect"
                                appendTo={document.body}
                                panelClassName="filter-treeselect-panel"
                            />
                        </Col>

                        <Col xs={4}>
                            <div className="filters-field-label">Order Type</div>
                            <Form.Select
                                className="filters-input"
                                value={f.orderType}
                                onChange={(e) => set("orderType", e.target.value)}
                            >
                                <option value="">Select</option>
                                <option value="DS">Dropship</option>
                                <option value="CRK">Consolidate / Repack</option>
                                <option value="M">Manufacturing</option>
                                <option value="KIT">Kit</option>
                                <option value="S">Stock</option>
                            </Form.Select>
                        </Col>

                        <Col xs={6}>
                            <div className="filters-field-label">Order Payment Type</div>
                            <Form.Select
                                className="filters-input"
                                value={f.paymentType}
                                onChange={(e) => set("paymentType", e.target.value)}
                            >
                                <option value="">Select</option>
                                <option value="invoiced">Invoiced</option>
                                <option value="pre-paid">Prepaid</option>
                                <option value="post-paid">Postpaid</option>
                            </Form.Select>
                        </Col>

                        <Col xs={6}>
                            <div className="filters-field-label">Status</div>
                            <Form.Select
                                className="filters-input"
                                value={f.status}
                                onChange={(e) => set("status", e.target.value)}
                            >
                                <option value="">All</option>
                                {STATUS_OPTIONS.map((s) => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </Form.Select>
                        </Col>
                    </Row>

                    <SectionHeader icon={IconBolt} type="specific" label="SPECIFIC" />

                    <Row className="g-3 mb-2 align-items-end">
                        <Col xs={6}>
                            <div className="filters-field-label">Vendor</div>
                            <Form.Select
                                className="filters-input"
                                value={f.vendor}
                                onChange={handleVendorChange}
                            >
                                <option value="">Select</option>
                                {(vendorlist?.data || []).map((v) => (
                                    <option key={v.id} value={v.id}>{v.display_name}</option>
                                ))}
                            </Form.Select>
                        </Col>

                        <Col xs={6}>
                            <div
                                onClick={() => setShowProductModal(true)}
                                className="filters-product-select"
                                style={{ cursor: "pointer" }}
                            >
                                <div className="filters-product-icon avatar bg-indigo-lt">
                                    <IconBox size={14} />
                                </div>
                                <div className="filters-product-text">
                                    <div className="fw-semibold form-label text-dark mb-0">
                                        {f.selectedProducts?.length || 0} products selected
                                    </div>
                                    <div className="text-specific">Click to edit selection</div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <SectionHeader icon={IconCalendarTime} type="dates" label="DATES" />

                    {dateRangeRow("Created Date", f.createdFrom, f.createdTo, "createdFrom", "createdTo", new Date())}
                    {dateRangeRow("Published Date", f.publishedFrom, f.publishedTo, "publishedFrom", "publishedTo")}
                    {dateRangeRow("Due Date", f.dueDateFrom, f.dueDateTo, "dueDateFrom", "dueDateTo")}
                </div>

                <div className="filters-footer">
                    <div className="small text-muted">
                        Live preview:{" "}
                        <span className="fw-bold text-orange">{matchCount}</span>{" "}
                        of <span className="fw-bold text-dark">{totalCount}</span> opportunities
                    </div>
                    <div className="d-flex gap-2">
                        <Button variant="light" onClick={onHide} className="border">Cancel</Button>
                        <Button onClick={handleApply} className="text-white border-0 btn btn-warning">
                            Apply Filters
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Filters;
